import * as React from 'react';
import { Line } from 'react-chartjs-2';
import {
  SALES_TREND,
  LOCATIONS_TREND,
  UNITS_TREND,
  SALES_TREND_PORTFOLIO,
  LOCATIONS_TREND_PORTFOLIO,
  UNITS_TREND_PORTFOLIO
} from 'apollo/queries';
import { TrendSalesType, TrendLocationsType, TrendUnitsType } from 'utils/types';
import { getData, formatTo } from 'utils/functions';
import QueryRetryWrapper from 'components/QueryRetryWrapper';
import moment from 'moment';
import Formats from 'utils/formats';

interface Coordinates {
  week: string;
  amount: number;
}

const extractProperty = (data: Coordinates[], property: string) => {
  return data.map(elem => {
    return property === 'week'
      ? moment.utc(elem[property], 'YYYY-MM-DDThh:mm:ssZ').format('MM/DD/YYYY')
      : elem[property];
  });
};

interface Props {
  clientID: string;
  globalFilter: object;
  type: string;
  portfolio?: boolean;
}

const getPortfolioQuery = (type: string) => {
  switch (type) {
    case TrendSalesType:
      return SALES_TREND_PORTFOLIO;
    case TrendLocationsType:
      return LOCATIONS_TREND_PORTFOLIO;
    case TrendUnitsType:
      return UNITS_TREND_PORTFOLIO;
    default:
      break;
  }
};

const getQuery = (type: string) => {
  switch (type) {
    case TrendSalesType:
      return SALES_TREND;
    case TrendLocationsType:
      return LOCATIONS_TREND;
    case TrendUnitsType:
      return UNITS_TREND;
    default:
      break;
  }
};

const getDisplayFormat = (type: string) => {
  switch (type) {
    case TrendSalesType:
      return 'currency';
    case TrendLocationsType:
    case TrendUnitsType:
      return 'number';
    default:
      break;
  }
};

const TrendChart: React.FC<Props> = ({ clientID, globalFilter, type, portfolio }) => {
  const displayFormat = getDisplayFormat(type);
  return (
    <QueryRetryWrapper
      query={portfolio ? getPortfolioQuery(type) : getQuery(type)}
      variables={{ clientID, globalFilter }}
      fetchPolicy="cache-first"
    >
      {({ data }) => {
        const weeks = extractProperty(getData(data), 'week');
        const amounts = extractProperty(getData(data), 'amount');
        return (
          <Line
            data={{
              labels: weeks,
              datasets: [
                {
                  data: amounts,
                  borderColor: '#3090F0',
                  fill: false
                }
              ]
            }}
            options={{
              legend: {
                display: false
              },
              scales: {
                yAxes: [
                  {
                    ticks: {
                      beginAtZero: true,
                      callback: function(value, _index, _values) {
                        return type === TrendSalesType
                          ? Formats.currency(value)
                          : Formats.number(value, 0, false);
                      }
                    }
                  }
                ],
                xAxes: [
                  {
                    type: 'time',
                    time: {
                      unit: 'week',
                      parser: 'MM/DD/YYYY'
                    },
                    ticks: {
                      minRotation: 45,
                      source: 'labels',
                      callback: function(value, _index, _values) {
                        const count = _values.length;
                        const endWeekIndex = count - 1;
                        if (count > 12 && _index !== endWeekIndex) {
                          return _index % 4 === 0 ? value : undefined;
                        }
                        return value;
                      }
                    }
                  }
                ]
              },
              tooltips: {
                callbacks: {
                  label: ({ value }) =>
                    `${type.replace(/[\W_]+/g, '')}: ${formatTo(displayFormat, value)}`,
                  labelColor: () => ({
                    borderColor: '#00a4f5',
                    backgroundColor: '#00a4f5'
                  }),
                  title: data => weeks[data[0].index]
                }
              }
            }}
          />
        );
      }}
    </QueryRetryWrapper>
  );
};

export default TrendChart;
